import { useState, useMemo } from "react";
import { Link, useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/logo.png";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { categoryConfig } from "@/lib/categoryConfig";
import { 
  getOrderedSeasonalSections, 
  isCurrentMonth, 
  isCurrentSeason 
} from "@/lib/seasonalHelpers";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/articles?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setIsMenuOpen(false);
    }
  };

  // Transform categoryConfig to menu format with dynamic seasonal ordering
  const menuData = useMemo(() => {
    return Object.entries(categoryConfig).map(([slug, config]) => {
      // Apply dynamic ordering for seasonal-guide menu
      const sections = slug === "seasonal-guide" 
        ? getOrderedSeasonalSections(config.sections)
        : config.sections;
      
      return {
        key: slug,
        title: config.title,
        href: `/category/${slug}`,
        sections: sections.map(section => {
          // Map review section titles to their landing pages
          const reviewSectionSlugs: Record<string, string> = {
            "Power Tools": "power-tools",
            "Garden Machinery": "garden-machinery",
            "Hand Tools": "hand-tools",
            "Garden Kit": "garden-kit",
            "Outdoor Furniture": "outdoor-furniture",
            "Heating & Cooling": "heating-cooling",
            "Garden Living": "garden-living",
            "BBQ & Outdoor Cooking": "bbq-outdoor-cooking",
            "Watering & Irrigation": "watering-irrigation",
            "Plants & Growing": "plants-growing",
            "Pets & Wildlife": "pets-wildlife",
            "Play & Leisure": "play-leisure",
            "Workshop & Safety": "workshop-safety",
            "Landscaping": "landscaping",
            "Seasonal": "seasonal",
            "By Brand": "brands",
          };

          const adviceSectionSlugs: Record<string, string> = {
            "Garden Care": "garden-care",
            "Garden Features": "garden-features",
            "Problem Solving": "problem-solving",
          };

          const sectionLandingSlug = slug === "reviews" 
            ? reviewSectionSlugs[section.title] 
            : slug === "advice" 
              ? adviceSectionSlugs[section.title] 
              : null;

          return {
            title: section.title,
            titleHref: sectionLandingSlug 
              ? (slug === "advice" ? `/advice/${sectionLandingSlug}` : `/reviews/${sectionLandingSlug}`) 
              : undefined,
            links: section.links.map(link => {
              const resolveHref = (l: { href?: string; slug: string }) =>
                l.href
                  ? l.href
                  : section.title === "By Brand"
                    ? `/brand/${l.slug}`
                    : `/category/${slug}?sub=${l.slug}`;
              return {
                name: link.name,
                href: resolveHref(link),
                children: link.children?.map(child => ({
                  name: child.name,
                  href: resolveHref(child),
                })),
              };
            }),
          };
        }),
      };
    });
  }, []);

  const toggleMobileSubmenu = (key: string) => {
    setExpandedMobileMenu(expandedMobileMenu === key ? null : key);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border/60">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logo} alt="Garden Review" className="h-9 w-auto" width={36} height={36} />
          </Link>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden xl:flex">
            <NavigationMenuList>
              {menuData.map((menu) => (
                <NavigationMenuItem key={menu.key}>
                  <NavigationMenuTrigger className="bg-transparent text-sm font-medium">
                    {menu.title}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className={cn("p-6 bg-popover", menu.sections.length > 5 ? "w-[1000px]" : menu.sections.length > 3 ? "w-[800px]" : "w-[600px]")}>
                      <div className={cn("grid gap-6", menu.sections.length > 5 ? "grid-cols-6" : menu.sections.length > 3 ? "grid-cols-4" : "grid-cols-3")}>
                        {menu.sections.map((section, idx) => {
                          const isSeasonal = menu.key === "seasonal-guide";
                          const isCurrentSeasonSection = isSeasonal && isCurrentSeason(section.title);
                          const visibleLinks = menu.key === "reviews" ? section.links.slice(0, 4) : section.links;
                          const showReviewMoreLink = menu.key === "reviews" && !!section.titleHref && section.links.length > 4;
                          
                          return (
                            <div key={idx}>
                              {section.titleHref ? (
                                <Link to={section.titleHref} className={cn(
                                  "font-semibold mb-3 text-sm flex items-center gap-2 hover:text-primary transition-colors",
                                  isCurrentSeasonSection ? "text-primary" : "text-foreground"
                                )}>
                                  {section.title}
                                </Link>
                              ) : (
                                <h4 className={cn(
                                  "font-semibold mb-3 text-sm flex items-center gap-2",
                                  isCurrentSeasonSection ? "text-primary" : "text-foreground"
                                )}>
                                  {section.title}
                                  {isCurrentSeasonSection && (
                                    <span className="text-[10px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                                      Now
                                    </span>
                                  )}
                                </h4>
                              )}
                              <ul className="space-y-2">
                                {visibleLinks.map((link, linkIdx) => {
                                  const isCurrentMonthLink = isSeasonal && isCurrentMonth(link.name);
                                  
                                  return (
                                    <li key={linkIdx}>
                                      <NavigationMenuLink asChild>
                                        <Link
                                          to={link.href}
                                          className={cn(
                                            "text-sm transition-colors block py-1 rounded-sm",
                                            isCurrentMonthLink
                                              ? "text-primary font-medium bg-primary/5 px-2 -mx-2 flex items-center justify-between"
                                              : link.children && link.children.length > 0
                                                ? "text-foreground font-medium hover:text-primary"
                                                : "text-muted-foreground hover:text-primary"
                                          )}
                                        >
                                          {link.name}
                                          {isCurrentMonthLink && (
                                            <span className="text-[10px] font-medium bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                                              This Month
                                            </span>
                                          )}
                                        </Link>
                                      </NavigationMenuLink>
                                      {link.children && link.children.length > 0 && (
                                        <ul className="mt-1 ml-3 space-y-1 border-l border-border/50 pl-3">
                                          {link.children.map((child, ci) => (
                                            <li key={ci}>
                                              <NavigationMenuLink asChild>
                                                <Link
                                                  to={child.href}
                                                  className="text-xs text-muted-foreground hover:text-primary transition-colors block py-0.5"
                                                >
                                                  {child.name}
                                                </Link>
                                              </NavigationMenuLink>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </li>
                                  );
                                })}
                                {showReviewMoreLink && (
                                  <li>
                                    <NavigationMenuLink asChild>
                                      <Link to={section.titleHref!} className="block rounded-md bg-primary/10 px-2 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/15">
                                        View more
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                )}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4 pt-4 border-t border-border">
                        <Link
                          to={menu.href}
                          className="text-sm font-medium text-primary hover:underline"
                        >
                          View all {menu.title.toLowerCase()} →
                        </Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>

          {/* Search and Mobile Menu */}
          <div className="flex items-center space-x-4">
            <form onSubmit={handleSearch} className="hidden sm:flex items-center space-x-2" role="search">
              <label htmlFor="header-search" className="sr-only">Search guides</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="header-search"
                  name="search"
                  type="search"
                  placeholder="Search guides..."
                  aria-label="Search guides"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-44 xl:w-56 h-9 rounded-full bg-muted/60 border-transparent focus-visible:bg-background focus-visible:border-input"
                />
              </div>
            </form>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="xl:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div id="mobile-navigation" className="xl:hidden border-t border-border bg-background max-h-[80vh] overflow-y-auto">
            <div className="py-2">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="px-4 py-2 sm:hidden" role="search">
                <label htmlFor="mobile-search" className="sr-only">Search guides</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="mobile-search"
                    name="search"
                    type="search"
                    placeholder="Search guides..."
                    aria-label="Search guides"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-full"
                  />
                </div>
              </form>

              {/* Mobile Menu Items */}
              {menuData.map((menu) => (
                <div key={menu.key} className="border-b border-border last:border-b-0">
                  <button
                    onClick={() => toggleMobileSubmenu(menu.key)}
                    className="flex items-center justify-between w-full px-4 py-3 text-left text-foreground hover:bg-muted/50 transition-colors"
                  >
                    <span className="font-medium">{menu.title}</span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform",
                        expandedMobileMenu === menu.key && "rotate-180"
                      )}
                    />
                  </button>

                  {expandedMobileMenu === menu.key && (
                    <div className="bg-muted/30 pb-2">
                      {menu.sections.map((section, idx) => {
                        const isSeasonal = menu.key === "seasonal-guide";
                        const isCurrentSeasonSection = isSeasonal && isCurrentSeason(section.title);
                        const visibleLinks = menu.key === "reviews" ? section.links.slice(0, 4) : section.links;
                        const showReviewMoreLink = menu.key === "reviews" && !!section.titleHref && section.links.length > 4;
                        
                        return (
                          <div key={idx} className="px-4 py-2">
                            <h5 className={cn(
                              "text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-2",
                              isCurrentSeasonSection 
                                ? "text-primary" 
                                : "text-muted-foreground"
                            )}>
                              {section.title}
                              {isCurrentSeasonSection && (
                                <span className="text-[9px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded normal-case tracking-normal">
                                  Now
                                </span>
                              )}
                            </h5>
                            <ul className="space-y-1">
                              {visibleLinks.map((link, linkIdx) => {
                                const isCurrentMonthLink = isSeasonal && isCurrentMonth(link.name);
                                
                                return (
                                  <li key={linkIdx}>
                                    <Link
                                      to={link.href}
                                      className={cn(
                                        "block py-1.5 pl-2 text-sm transition-colors",
                                        isCurrentMonthLink
                                          ? "text-primary font-medium bg-primary/5 rounded flex items-center justify-between pr-2"
                                          : "text-foreground hover:text-primary"
                                      )}
                                      onClick={() => setIsMenuOpen(false)}
                                    >
                                      {link.name}
                                      {isCurrentMonthLink && (
                                        <span className="text-[9px] font-medium bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                                          This Month
                                        </span>
                                      )}
                                    </Link>
                                    {link.children && link.children.length > 0 && (
                                      <ul className="ml-3 mt-1 space-y-1 border-l border-border/50 pl-3">
                                        {link.children.map((child, ci) => (
                                          <li key={ci}>
                                            <Link
                                              to={child.href}
                                              className="block py-1 text-xs text-muted-foreground hover:text-primary"
                                              onClick={() => setIsMenuOpen(false)}
                                            >
                                              {child.name}
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </li>
                                );
                              })}
                              {showReviewMoreLink && (
                                <li>
                                  <Link
                                    to={section.titleHref!}
                                    className="block rounded-md bg-primary/10 px-3 py-2 text-sm font-semibold text-primary"
                                    onClick={() => setIsMenuOpen(false)}
                                  >
                                    View more
                                  </Link>
                                </li>
                              )}
                            </ul>
                          </div>
                        );
                      })}
                      <div className="px-4 pt-2">
                        <Link
                          to={menu.href}
                          className="text-sm font-medium text-primary"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          View all {menu.title.toLowerCase()} →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
